import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTwitter } from '@soundxyz/font-awesome/free-brands-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import type { PostModalSource } from '@soundxyz/utils/analytics';
import { Button } from '@components/main-components/button/Button';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useModal } from '@context/modal';
import {
  AfterFeedPostReleaseInfoFragmentDoc,
  type FragmentType,
  getFragment,
} from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { getPostOpenGraphImage } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { TwitterPostShareLink } from '../ShareModalHelpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment AfterFeedPostReleaseInfo on Release {
    id
    publicAffiliateFeeBPS
    artist {
      id
      user {
        id
        twitterHandle
      }
    }
  }
`);

const CALL_TO_ACTION_HEADER_WITH_CURATOR_REWARDS =
  'Share to earn curator rewards for every primary sale from your link.';
const CALL_TO_ACTION_HEADER_NO_CURATOR_REWARDS = 'Share to amplify the reach of your post.';

const OG_WIDTH = 1200;
const OG_HEIGHT = 635;

const blurOpenGraph = blurImage(OG_WIDTH, OG_HEIGHT);

export function AfterFeedPostModal({
  release,
  releasePostWebappUri,
  postId,
  sourceLocation,
}: {
  release: FragmentType<typeof AfterFeedPostReleaseInfoFragmentDoc>;
  releasePostWebappUri: string;
  sourceLocation: PostModalSource;
  postId: string;
}) {
  const {
    publicAffiliateFeeBPS,
    artist: {
      user: { twitterHandle },
    },
  } = getFragment(AfterFeedPostReleaseInfoFragmentDoc, release);

  const { closeModal } = useModal();
  const openGraphImage = useMemo(() => {
    return getPostOpenGraphImage({
      postId,
    });
  }, [postId]);

  return (
    <StandardModal
      closeModal={() => {
        closeModal();
        trackClickEvent({
          event: 'After Post Share Modal Closed',
          properties: {
            associationType: 'Post',
            associationId: postId,
            sourceComponent: sourceLocation,
          },
        });
      }}
      bodyWithoutPaddingBottom
      body={
        <div className="flex flex-col gap-4">
          <h2>
            {publicAffiliateFeeBPS
              ? CALL_TO_ACTION_HEADER_WITH_CURATOR_REWARDS
              : CALL_TO_ACTION_HEADER_NO_CURATOR_REWARDS}
          </h2>
          {openGraphImage ? (
            <div className="h-auto w-full">
              <NextFutureImage
                placeholder="blur"
                blurDataURL={blurOpenGraph}
                className="rounded-xl"
                src={openGraphImage}
                alt="Shared Post Image"
                width={OG_HEIGHT}
                height={OG_HEIGHT}
                unoptimized
              />
            </div>
          ) : null}
        </div>
      }
      title="Posted!"
      callToAction={
        <div className="flex w-full flex-row gap-3 [&_a]:w-full">
          <Button
            variant="secondary"
            className="px-[10px] text-base-l"
            size="L"
            fullWidth
            label="View Post"
            href={webappUriPath(releasePostWebappUri)}
            onClick={() => {
              trackClickEvent({
                event: 'After Post Share Modal View Post',
                properties: {
                  associationType: 'Post',
                  associationId: postId,
                  sourceComponent: sourceLocation,
                },
              });
              closeModal();
            }}
          />
          <Button
            variant="primary"
            className="px-[10px] text-base-l"
            leadingIcon={<FontAwesomeIcon size="lg" icon={faTwitter} />}
            size="L"
            fullWidth
            label="Share"
            href={TwitterPostShareLink({
              webappUri: releasePostWebappUri,
              artistTwitterHandle: twitterHandle,
            })}
            onClick={() => {
              trackClickEvent({
                event: 'After Post Share Modal Share To Twitter',
                properties: {
                  associationType: 'Post',
                  associationId: postId,
                  sourceComponent: sourceLocation,
                },
              });
            }}
          />
        </div>
      }
    />
  );
}
